import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Star, Baby, Gamepad2, Book, Palette } from 'lucide-react';

export default function Artikel25GeschenkeKinder() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            Die 25 besten Eid Geschenke für Kinder 2026
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Von islamischen Kinderbüchern über Spielzeug bis zu kreativen DIY-Sets – 
            entdecke die schönsten Geschenkideen, die Kinderaugen zum Leuchten bringen.
          </p>
        </div>
      </section>

      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
              Eid ist für Kinder ein magischer Tag voller Vorfreude, Überraschungen und Freude. 
              Das richtige Geschenk macht diesen besonderen Tag unvergesslich und vermittelt 
              gleichzeitig die Bedeutung des Festes. In diesem umfassenden Guide haben wir 
              die 25 besten Eid-Geschenke für Kinder zusammengestellt – sortiert nach Alter 
              und Interessen.
            </p>

            {/* Table of Contents */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4">📖 Inhaltsverzeichnis</h2>
              <ul className="space-y-2 text-[#4A5D4F]">
                <li><a href="#kleinkinder" className="hover:underline">1. Geschenke für Kleinkinder (0-3 Jahre)</a></li>
                <li><a href="#vorschule" className="hover:underline">2. Geschenke für Vorschulkinder (3-6 Jahre)</a></li>
                <li><a href="#grundschule" className="hover:underline">3. Geschenke für Grundschulkinder (6-10 Jahre)</a></li>
                <li><a href="#teenager" className="hover:underline">4. Geschenke für ältere Kinder (10-14 Jahre)</a></li>
                <li><a href="#lernspiele" className="hover:underline">5. Islamische Lernspiele & Bücher</a></li>
                <li><a href="#kreativ" className="hover:underline">6. Kreative & DIY Geschenke</a></li>
                <li><a href="#eidi" className="hover:underline">7. Eidi: Geldgeschenke richtig verpacken</a></li>
              </ul>
            </div>

            {/* Section 1 - Kleinkinder */}
            <section id="kleinkinder" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Baby className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  1. Geschenke für Kleinkinder (0-3 Jahre)
                </h2>
              </div>
              
              <p className="text-[#4B5563] leading-relaxed mb-6">
                Für die Kleinsten eignen sich weiche, sichere Spielzeuge und erste Bücher. 
                In diesem Alter geht es vor allem um sensorische Erfahrungen und Kuscheln.
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">1. Plüschtier-Schaf</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Perfekt für Eid ul Adha! Ein kuscheliges Schaf-Stofftier ist nicht nur süß, 
                    sondern hat auch eine symbolische Bedeutung. Ideal zum Kuscheln und als 
                    Erinnerung an das Fest.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">2. Stoffbuch mit islamischen Motiven</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Weiche Bücher mit Mond- und Sternenmotiven, die Babys anfassen und erkunden können. 
                    Fördert die sensorische Entwicklung und führt spielerisch an islamische Symbole heran.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">3. Holzspielzeug mit arabischen Buchstaben</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Greifpuzzles oder Stapelspiele mit arabischen Buchstaben. Nachhaltig, sicher 
                    und ein erster Kontakt mit der arabischen Schrift.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">4. Spieluhr mit Nasheeds</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Eine Spieluhr, die sanfte islamische Melodien oder Nasheeds spielt. 
                    Beruhigend zum Einschlafen und kulturell wertvoll.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 2 - Vorschulkinder */}
            <section id="vorschule" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Palette className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  2. Geschenke für Vorschulkinder (3-6 Jahre)
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                In diesem Alter entwickeln Kinder ihre Fantasie und lieben es, die Welt zu entdecken. 
                Bücher, kreative Spielsets und erste Lernspiele sind perfekt.
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">5. "Mama, was ist Ramadan?" Kinderbuch</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Ein wunderschön illustriertes Buch, das Kindern den Ramadan und Eid erklärt. 
                    72 Seiten mit Geschichten über Fasten, Geduld und Barmherzigkeit.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">6. Eid-Bastelset</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Alles zum Basteln von Eid-Karten, Laternen und Girlanden. Fördert Kreativität 
                    und macht das Fest zu einem aktiven Erlebnis.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">7. Arabisches Alphabet-Puzzle</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Bunte Holzpuzzles mit den 28 arabischen Buchstaben. Spielerisch Arabisch lernen 
                    und gleichzeitig die Feinmotorik schulen.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">8. Ramadan-Kalender</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    30 Türchen mit kleinen Überraschungen für jeden Tag des Ramadan. 
                    Baut Vorfreude auf und macht das Fasten greifbar.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">9. Kinderküche-Zubehör "Iftar-Set"</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Spielzeug-Datteln, Mini-Teekanne und Teller für die Kinderküche. 
                    So können die Kleinen ihr eigenes Iftar "kochen".
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3 - Grundschulkinder */}
            <section id="grundschule" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Book className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  3. Geschenke für Grundschulkinder (6-10 Jahre)
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                Grundschulkinder können schon lesen, lieben Spiele mit Regeln und interessieren 
                sich für Wissen. Perfekt für islamische Lernspiele und Bücher mit mehr Text.
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">10. SIRA Quiz-Karten</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    52 Quizkarten über das Leben des Propheten Muhammad (s.a.s.). Perfekt für 
                    Spieleabende mit der Familie und zum spielerischen Lernen.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">11. Mein erstes Gebetsbuch</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Kindgerechte Anleitung zum Gebet mit Illustrationen und einfachen Erklärungen. 
                    Hilft Kindern, die fünf täglichen Gebete zu lernen.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">12. Kinder-Gebetsteppich</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Ein kleiner, bunter Gebetsteppich mit Motiven und Markierungen für Hand- und 
                    Fußposition. Personalisierbar mit dem Namen des Kindes.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">13. Islamisches Brettspiel</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Spiele wie "Hajj Journey" oder "5 Pillars" vermitteln islamisches Wissen 
                    spielerisch. Ideal für die ganze Familie.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">14. Gutes-Taten-Tagebuch</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Ein Tagebuch, in dem Kinder ihre guten Taten während Ramadan festhalten können. 
                    Fördert Reflexion und Dankbarkeit.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 4 - Ältere Kinder */}
            <section id="teenager" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Gamepad2 className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  4. Geschenke für ältere Kinder (10-14 Jahre)
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                Ältere Kinder und Teenager haben eigene Interessen und schätzen "erwachsenere" 
                Geschenke. Technologie, Bücher und persönliche Accessoires kommen gut an.
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">15. Quran mit Übersetzung (Jugendausgabe)</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Eine schöne Quran-Ausgabe mit verständlicher deutscher Übersetzung. 
                    Ein bedeutungsvolles Geschenk für das spirituelle Wachstum.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">16. Hochwertige Tasbih (Gebetskette)</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Eine elegante Gebetskette aus Holz, Stein oder mit Halbedelsteinen. 
                    Praktisch und ein schönes Accessoire zugleich.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">17. Islamische Jugendbücher</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Bücher über Propheten, islamische Geschichte oder zeitgenössische muslimische 
                    Vorbilder. Inspirierend und lehrreich.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">18. Smartwatch oder Kopfhörer</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Technische Gadgets sind bei Teenagern beliebt. Kann für Gebets-Apps oder 
                    zum Hören von Quran-Rezitationen genutzt werden.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#D4C5A9]" />
                    <h4 className="font-medium text-[#2C332E]">19. Gutschein (Amazon, Zalando, etc.)</h4>
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Teenager wissen selbst am besten, was sie wollen. Ein Gutschein gibt ihnen 
                    die Freiheit, sich etwas Besonderes auszusuchen.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5 - Lernspiele */}
            <section id="lernspiele" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Book className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  5. Islamische Lernspiele & Bücher
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                Diese Geschenke verbinden Spaß mit Lernen und helfen Kindern, ihre islamische 
                Identität zu stärken:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">20. Memory mit islamischen Motiven</h4>
                  <p className="text-sm text-[#4B5563]">Moscheen, Monde, Laternen – spielend islamische Symbole kennenlernen.</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">21. Propheten-Geschichten (Hörbuch)</h4>
                  <p className="text-sm text-[#4B5563]">Für lange Autofahrten oder vor dem Einschlafen.</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">22. Quran-Lern-App (Jahresabo)</h4>
                  <p className="text-sm text-[#4B5563]">Interaktiv Arabisch und Quran-Rezitation lernen.</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">23. Islamisches Malbuch</h4>
                  <p className="text-sm text-[#4B5563]">Moscheen, Kalligraphie und Ornamente zum Ausmalen.</p>
                </div>
              </div>
            </section>

            {/* Section 6 - Kreativ */}
            <section id="kreativ" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Palette className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  6. Kreative & DIY Geschenke
                </h2>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">24. Eid-Karten-Bastelset</h4>
                  <p className="text-sm text-[#4B5563]">
                    Alles zum Gestalten eigener Eid-Mubarak-Karten: Glitzer, Stempel, Aufkleber 
                    und Blanko-Karten. Perfekt für kreative Stunden vor dem Fest.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">25. Laternen-Bastelset</h4>
                  <p className="text-sm text-[#4B5563]">
                    Materialien und Anleitung zum Basteln einer eigenen Ramadan-Laterne. 
                    Ein leuchtendes Andenken an das Fest!
                  </p>
                </div>
              </div>
            </section>

            {/* Section 7 - Eidi */}
            <section id="eidi" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  7. Eidi: Geldgeschenke richtig verpacken
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                Geldgeschenke (Eidi) sind traditionell und bei Kindern sehr beliebt. 
                So machen Sie es besonders:
              </p>

              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Eid-Umschläge:</strong> Spezielle Umschläge mit "Eid Mubarak" und islamischen Motiven</li>
                <li><strong>Origami-Geldscheine:</strong> Zu Sternen oder Herzen gefaltet</li>
                <li><strong>Schatzkiste:</strong> Geld in einer kleinen Truhe mit Süßigkeiten</li>
                <li><strong>Ballon-Überraschung:</strong> Geldschein in einem Ballon verstecken</li>
              </ul>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">💡 Richtwerte für Eidi</h4>
                <ul className="text-[#4B5563] space-y-1 text-sm">
                  <li>• Neffen/Nichten: 10-50€</li>
                  <li>• Kinder von Freunden: 5-20€</li>
                  <li>• Nachbarskinder: 5-10€</li>
                </ul>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">
                Fazit: Das perfekte Eid-Geschenk für jedes Kind
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das beste Eid-Geschenk ist eines, das zum Kind passt und von Herzen kommt. 
                Ob lehrreich, kreativ oder einfach nur zum Spaß – wichtig ist, dass es Freude 
                bereitet und positive Erinnerungen an das Fest schafft.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Denken Sie daran: Kinder schätzen auch die Zeit, die Sie mit ihnen verbringen. 
                Ein gemeinsames Basteln, Spielen oder Vorlesen kann genauso wertvoll sein wie 
                ein gekauftes Geschenk.
              </p>
              <p className="text-xl text-[#4A5D4F] font-medium">
                Eid Mubarak! Mögen Ihre Kinder ein gesegnetes Fest erleben! 🌙
              </p>
            </section>

            {/* Internal Links */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-8">
              <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-4">Weitere Artikel</h3>
              <ul className="space-y-2">
                <li><Link to="/ratgeber/beste-eid-geschenke-2026" className="text-[#4A5D4F] hover:underline">→ Die besten Eid Geschenke 2026</Link></li>
                <li><Link to="/ratgeber/ramadan-geschenke" className="text-[#4A5D4F] hover:underline">→ Ramadan Geschenke Guide</Link></li>
                <li><Link to="/ratgeber/was-ist-eid" className="text-[#4A5D4F] hover:underline">→ Was ist Eid? Ein Leitfaden</Link></li>
              </ul>
            </div>

            {/* CTA */}
            <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
              <h3 className="font-serif text-2xl font-medium text-white mb-4">
                Entdecken Sie unsere Kinderprodukte
              </h3>
              <p className="text-white/80 mb-6">
                Handverlesene Eid-Geschenke für Kinder jeden Alters
              </p>
              <Link
                to="/geschenke/kinder"
                className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors"
              >
                Jetzt entdecken
                <Gift className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
